import { useState, useCallback } from 'react';
import type { SheetTemplate, TemplateSection, TokenBinding } from '@roll1/shared';
import { builtinTemplates } from '@roll1/shared/builtinTemplates';

export interface UseTemplatesReturn {
  templates: SheetTemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<SheetTemplate[]>>;
  createTemplate: (name: string, sections: TemplateSection[], tokenBinding: TokenBinding) => void;
  updateTemplate: (templateId: string, changes: { name?: string; sections?: TemplateSection[]; tokenBinding?: TokenBinding }) => void;
  deleteTemplate: (templateId: string) => void;
  handleCreated: (template: SheetTemplate) => void;
  handleUpdated: (templateId: string, changes: { name?: string; sections?: TemplateSection[]; tokenBinding?: TokenBinding }) => void;
  handleDeleted: (templateId: string) => void;
}

export function useTemplates(): UseTemplatesReturn {
  const [templates, setTemplates] = useState<SheetTemplate[]>(builtinTemplates);

  // No-op local methods — sends go through mp.send wrappers
  const createTemplate = useCallback((_name: string, _sections: TemplateSection[], _tokenBinding: TokenBinding) => {}, []);
  const updateTemplate = useCallback((_templateId: string, _changes: { name?: string; sections?: TemplateSection[]; tokenBinding?: TokenBinding }) => {}, []);
  const deleteTemplate = useCallback((_templateId: string) => {}, []);

  const handleCreated = useCallback((template: SheetTemplate) => {
    setTemplates(prev => {
      if (prev.some(t => t.id === template.id)) return prev;
      return [...prev, template];
    });
  }, []);

  const handleUpdated = useCallback((templateId: string, changes: { name?: string; sections?: TemplateSection[]; tokenBinding?: TokenBinding }) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      return { ...t, ...changes };
    }));
  }, []);

  const handleDeleted = useCallback((templateId: string) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
  }, []);

  return {
    templates,
    setTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    handleCreated,
    handleUpdated,
    handleDeleted,
  };
}
